<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useRouter, useRoute } from 'vue-router';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
  isOnTrashView,
  isOnFoldersView,
  isOnParticipatingView,
} from 'dashboard/store/modules/conversations/helpers/actionHelpers';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import PriorityAction from '../../buttons/PriorityAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();
const route = useRoute();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const deleteConversationDialogRef = ref(null);
const router = useRouter();
const currentChat = computed(() => store.getters.getSelectedChat);

const deleteDialogTitle = computed(() => {
  return `Delete conversation #${currentChat.value?.display_id || ''}`;
});

const conversationUrl = computed(() => {
  const accountId = store.getters.getCurrentAccountId;
  return `/app/accounts/${accountId}/conversations/${currentChat.value.id}`;
});

const actionMenuItems = computed(() => {
  const items = [];

  // Mark as Read/Unread
  if (currentChat.value.unread_count > 0) {
    items.push({
      icon: 'i-lucide-mail',
      label: 'Mark as read',
      action: 'mark_as_read',
      value: 'mark_as_read',
    });
  } else {
    items.push({
      icon: 'i-lucide-mail-open',
      label: 'Mark as unread',
      action: 'mark_as_unread',
      value: 'mark_as_unread',
    });
  }

  // Mute/Unmute
  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: 'Mute conversation',
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: 'Unmute conversation',
      action: 'unmute',
      value: 'unmute',
    });
  }

  // Send Transcript
  items.push({
    icon: 'i-lucide-share',
    label: 'Send transcript',
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Open in New Tab
  items.push({
    icon: 'i-lucide-external-link',
    label: 'Open in new tab',
    action: 'open_new_tab',
    value: 'open_new_tab',
  });

  // Copy Link
  items.push({
    icon: 'i-lucide-link',
    label: 'Copy link',
    action: 'copy_link',
    value: 'copy_link',
  });

  // Delete
  items.push({
    icon: 'i-lucide-trash-2',
    label: 'Delete conversation',
    action: 'delete',
    value: 'delete',
  });

  return items;
});

const redirectToConversationList = () => {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId, id },
    name,
  } = route;

  let conversationType = '';
  let customViewId = undefined;
  
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  } else if (isOnTrashView({ route: { name } })) {
    conversationType = 'trash';
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = 'participating';
  } else if (isOnFoldersView({ route: { name } })) {
    // For folder views, the 'id' param is the customViewId
    customViewId = id;
  }
  
  router.push(
    conversationListPageURL({
      accountId,
      conversationType,
      customViewId,
      inboxId,
      label,
      teamId,
    })
  );
};

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'mark_as_read') {
    await store.dispatch('markMessagesRead', { conversationId: currentChat.value.id });
    useAlert('Marked as read');
  } else if (action === 'mark_as_unread') {
    try {
      await store.dispatch('markMessagesUnread', { id: currentChat.value.id });
      redirectToConversationList();
      useAlert('Marked as unread');
    } catch (error) {
      // Ignore error
    }
  } else if (action === 'mute') {
    await store.dispatch('muteConversation', currentChat.value.id);
    useAlert('Conversation muted successfully');
  } else if (action === 'unmute') {
    await store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert('Conversation unmuted successfully');
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'open_new_tab') {
    window.open(conversationUrl.value, '_blank');
  } else if (action === 'copy_link') {
    copyTextToClipboard(window.location.origin + conversationUrl.value);
    useAlert('Link copied to clipboard');
  } else if (action === 'delete') {
    deleteConversationDialogRef.value.open();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

const deleteConversation = async () => {
  try {
    await store.dispatch('deleteConversation', currentChat.value.id);
    // Navigate away BEFORE closing dialog to avoid errors
    redirectToConversationList();
    deleteConversationDialogRef.value?.close();
    useAlert('Conversation deleted successfully');
  } catch (error) {
    deleteConversationDialogRef.value?.close();
    useAlert('Failed to delete conversation');
  }
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <PriorityAction />
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="deleteDialogTitle"
      description="Are you sure you want to delete this conversation?"
      confirm-button-label="Delete"
      @confirm="deleteConversation"
    />
  </div>
</template>
